import React, { useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { ArrowRight } from 'lucide-react';
import LogoMark, { LOGO_VIEW, RING } from './LogoMark';
import RingLight from './RingLight';
import MagneticLink from './MagneticLink';
import ringGeometry from './ringGeometry';
import { prefersReduced, scrollToEl, smooth } from './motion';
import { portalToElement } from '../../site/Portal';

gsap.registerPlugin(ScrollTrigger, useGSAP);

const WORDS = ['apps.', 'plataformas.', 'sistemas.', 'produtos.'];
const INTRO_KEY = 'idz-intro-v3';

// A abertura roda uma vez por sessão (e nunca com reduced-motion).
function shouldPlayIntro() {
  if (typeof window === 'undefined' || prefersReduced()) return false;
  try {
    return !window.sessionStorage.getItem(INTRO_KEY);
  } catch (e) {
    return true;
  }
}

function Hero() {
  const root = useRef(null);
  const logoRef = useRef(null);
  const [intro, setIntro] = useState(shouldPlayIntro);
  // Estado do anel de luz, escrito pelo GSAP e lido pelo shader a cada frame.
  const ring = useRef({ intro: 0, draw: 0, scroll: 0, dirty: true, kick: null }).current;

  useGSAP(
    () => {
      const q = gsap.utils.selector(root);
      const reduced = prefersReduced();
      const html = document.documentElement;
      const kick = () => ring.kick && ring.kick();

      // ---------- Título: palavras sobem de dentro da máscara ----------
      const revealText = () =>
        gsap
          .timeline()
          .to(q('.hv3-rise'), { y: 0, rotate: 0, duration: 1.25, ease: 'expo.out', stagger: 0.075 })
          .to(
            q('.hv3-fade'),
            { autoAlpha: 1, y: 0, filter: 'blur(0px)', duration: 1, ease: 'expo.out', stagger: 0.09 },
            '-=0.95'
          )
          .add(startRotator, '-=0.6');

      // ---------- Palavra que troca: sai rápido, entra com calma, blur entre as duas ----------
      let rotCall;
      function startRotator() {
        const items = q('.hv3-rot-word');
        let i = 0;
        gsap.set(items, { yPercent: 110, autoAlpha: 0 });
        gsap.set(items[0], { yPercent: 0, autoAlpha: 1 });
        const cycle = () => {
          const cur = items[i];
          i = (i + 1) % items.length;
          const nxt = items[i];
          if (reduced) {
            gsap.to(cur, { autoAlpha: 0, duration: 0.3 });
            gsap.fromTo(nxt, { yPercent: 0, autoAlpha: 0 }, { autoAlpha: 1, duration: 0.4 });
          } else {
            gsap.to(cur, { yPercent: -110, autoAlpha: 0, filter: 'blur(10px)', duration: 0.45, ease: 'power3.in' });
            gsap.fromTo(
              nxt,
              { yPercent: 110, autoAlpha: 0, filter: 'blur(10px)' },
              { yPercent: 0, autoAlpha: 1, filter: 'blur(0px)', duration: 0.85, ease: 'expo.out', delay: 0.2 }
            );
          }
          rotCall = gsap.delayedCall(2.8, cycle);
        };
        rotCall = gsap.delayedCall(2.4, cycle);
      }

      // ---------- Saída do hero: o anel cresce e se abre, o texto sobe ----------
      const exit = () => {
        ScrollTrigger.create({
          trigger: root.current,
          start: 'top top',
          end: 'bottom top',
          scrub: true,
          onUpdate: (self) => {
            ring.scroll = self.progress;
            kick();
          },
        });
        if (!reduced) {
          gsap.to(q('.hv3-hero-copy'), {
            yPercent: -22,
            autoAlpha: 0,
            ease: 'none',
            scrollTrigger: { trigger: root.current, start: 'top top', end: '75% top', scrub: true },
          });
        }
      };

      if (reduced) {
        ring.intro = 1;
        ring.draw = 1;
        kick();
        startRotator();
        exit();
        return () => rotCall && rotCall.kill();
      }

      if (!intro) {
        // Visita repetida: sem abertura, o próprio anel de luz se desenha.
        gsap.timeline({ onUpdate: kick })
          .to(ring, { intro: 1, duration: 0.8, ease: 'power2.out' }, 0)
          .to(ring, { draw: 1, duration: 1.8, ease: 'power3.inOut' }, 0.05)
          .add(revealText(), 0.35);
        exit();
        return () => rotCall && rotCall.kill();
      }

      // ---------- Abertura: o logo se desenha e vira o anel de luz ----------
      const logo = logoRef.current;
      const path = logo.querySelector('.lm-ring');
      const word = logo.querySelector('.lm-word');
      const maskRect = logo.querySelector('.lm-mask-rect');
      const len = path.getTotalLength();
      const origin = `${(RING.cx / LOGO_VIEW.w) * 100}% ${(RING.cy / LOGO_VIEW.h) * 100}%`;

      html.classList.add('hv3-intro-on');
      if (smooth.lenis) smooth.lenis.stop();
      try {
        window.sessionStorage.setItem(INTRO_KEY, '1');
      } catch (e) {
        /* sessão privada: tudo bem repetir */
      }

      gsap.set(path, { strokeDasharray: len, strokeDashoffset: len });
      // A borda em degradê da máscara começa antes do "I" e varre até o fim do nome.
      gsap.set(maskRect, { attr: { x: -624 } });
      gsap.set(word, { opacity: 0, x: -10 });
      gsap.set(logo, { transformOrigin: origin, rotate: -50, scale: 0.9 });

      // Onde o anel precisa pousar (mesma geometria que o shader usa).
      const flight = () => {
        const rect = logo.getBoundingClientRect();
        const s = rect.width / LOGO_VIEW.w;
        const hero = root.current.getBoundingClientRect();
        const geo = ringGeometry(hero.width, hero.height);
        return {
          x: hero.left + geo.cx - (rect.left + RING.cx * s),
          y: hero.top + geo.cy - (rect.top + RING.cy * s),
          scale: geo.r / (RING.r * s),
        };
      };
      let target = null;

      const finish = () => {
        html.classList.remove('hv3-intro-on');
        if (smooth.lenis) smooth.lenis.start();
        setIntro(false);
      };

      const tl = gsap.timeline({ onComplete: finish });
      tl.to(path, { strokeDashoffset: 0, duration: 1.05, ease: 'power3.inOut' }, 0.15)
        .to(logo, { rotate: 0, scale: 1, duration: 1.3, ease: 'expo.out' }, 0.1)
        .to(word, { opacity: 1, x: 0, duration: 0.9, ease: 'expo.out' }, 0.55)
        .to(maskRect, { attr: { x: 0 }, duration: 1.05, ease: 'power3.out' }, 0.55)
        .addLabel('fly', 1.55)
        .to(word, { opacity: 0, x: -24, duration: 0.3, ease: 'power2.in' }, 'fly')
        .to(
          logo,
          {
            x: () => (target = target || flight()).x,
            y: () => (target = target || flight()).y,
            scale: () => (target = target || flight()).scale,
            // A abertura do logo é inclinada 4,5° para cima; a do anel de luz é centrada.
            rotate: 4.5,
            duration: 1,
            ease: 'expo.inOut',
          },
          'fly'
        )
        .to(ring, { intro: 1, duration: 0.6, ease: 'power2.out', onUpdate: kick }, 'fly+=0.55')
        .set(ring, { draw: 1 }, 'fly+=0.55')
        .to('.hv3-intro', { backgroundColor: 'rgba(5, 8, 7, 0)', duration: 0.55, ease: 'power2.out' }, 'fly+=0.6')
        .to(logo, { opacity: 0, filter: 'blur(6px)', duration: 0.4, ease: 'power2.out' }, 'fly+=0.78')
        .add(revealText(), 'fly+=0.7');

      // Qualquer clique ou tecla acelera a abertura.
      const skip = () => tl.timeScale(4);
      window.addEventListener('pointerdown', skip, { once: true });
      window.addEventListener('keydown', skip, { once: true });

      exit();

      return () => {
        window.removeEventListener('pointerdown', skip);
        window.removeEventListener('keydown', skip);
        html.classList.remove('hv3-intro-on');
        if (smooth.lenis) smooth.lenis.start();
        if (rotCall) rotCall.kill();
      };
    },
    // A abertura só é montada uma vez; o fim dela desmonta o overlay sem reiniciar nada.
    { scope: root, dependencies: [] }
  );

  const goToWork = (e) => {
    e.preventDefault();
    scrollToEl('#trabalho');
  };
  // Atravessa o portal direto até a roda: sem a sensação de rolar a página inteira.
  const goToStart = (e) => {
    e.preventDefault();
    portalToElement('#comecar', e);
  };

  return (
    <section className="hv3-hero" ref={root} aria-labelledby="hero-title">
      <RingLight state={ring} />

      <div className="hv3-wrap hv3-hero-copy">
        <h1 id="hero-title" className="hv3-h1">
          <span className="hv3-sr">Transformamos ideias em apps, plataformas, sistemas e produtos.</span>
          <span aria-hidden className="hv3-h1-vis">
            <span className="hv3-line">
              <span className="hv3-mask"><span className="hv3-rise">Transformamos</span></span>{' '}
              <span className="hv3-mask"><span className="hv3-rise">ideias</span></span>
            </span>
            <span className="hv3-line">
              <span className="hv3-mask"><span className="hv3-rise">em</span></span>{' '}
              <span className="hv3-mask hv3-rot">
                {WORDS.map((w, i) => (
                  <span className={`hv3-rot-word ${i === 0 ? 'hv3-rise' : ''}`} key={w}>
                    {w}
                  </span>
                ))}
              </span>
            </span>
          </span>
        </h1>

        <p className="hv3-lede hv3-fade">
          Desenhamos, desenvolvemos e lançamos produtos digitais para empresas e
          startups. Do primeiro esboço à loja de apps.
        </p>

        <div className="hv3-actions hv3-fade">
          <MagneticLink href="#comecar" variant="primary" onClick={goToStart}>
            Começar um projeto
            <ArrowRight size={18} strokeWidth={2} aria-hidden />
          </MagneticLink>
          <MagneticLink href="#trabalho" variant="ghost" onClick={goToWork}>
            Ver projetos
          </MagneticLink>
        </div>
      </div>

      {intro && (
        <div className="hv3-intro" aria-hidden>
          <LogoMark ref={logoRef} className="hv3-intro-logo" idPrefix="intro" />
        </div>
      )}
    </section>
  );
}

export default Hero;
